import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/Modal";
import { TrackForm } from "./TrackForm";
import { Track, CreateTrackRequest } from "@/types";
import { useTrackForm, useCreateTrack, useUpdateTrack } from "@/hooks/useTracks";

interface TrackModalProps {
  isOpen: boolean;
  onClose: () => void;
  track?: Track;
  mode: "create" | "edit";
}

/**
 * Create/edit modal for tracks. Viewing a track is the dedicated
 * TrackDetailPage (/tracks/:trackId); this modal deliberately has no view
 * mode. Track mutations are admin-only on the backend.
 */
export const TrackModal: React.FC<TrackModalProps> = ({
  isOpen,
  onClose,
  track,
  mode,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  const { trackData, errors, validate, updateField, resetForm } =
    useTrackForm(track);
  const createTrackMutation = useCreateTrack();
  const updateTrackMutation = useUpdateTrack();

  const isEditing = mode === "edit";
  const isSubmitting =
    createTrackMutation.isPending || updateTrackMutation.isPending;

  React.useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      if (isEditing && track) {
        await updateTrackMutation.mutateAsync({
          id: track.id,
          ...(trackData as CreateTrackRequest),
        });
      } else {
        await createTrackMutation.mutateAsync(trackData as CreateTrackRequest);
      }
      onClose();
    } catch (error) {
      console.error("Error saving track:", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing
          ? t("pages:tracks.modal.editTrack")
          : t("pages:tracks.modal.createNewTrack")
      }
      size="lg"
    >
      <TrackForm
        trackData={trackData}
        errors={errors}
        onUpdateField={updateField}
        onSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isSubmitting}
        isEditing={isEditing}
      />
    </Modal>
  );
};
